import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { submitOrderStatus, flushQueue, onQueueChange } from "@/lib/offlineQueue";
import { useSocket } from "@/lib/socket";
import { useFeatures, FeatureOff } from "@/lib/features";
import KDSHistory, { buildHistory } from "@/components/kds/KDSHistory";
import KitchenLossDialog from "@/components/kds/KitchenLossDialog";
import { useAuth } from "@/context/AuthContext";
import { playChime, unlockAudio, isAudioUnlocked, subscribeAudio } from "@/lib/chime";
import { ChefHat, Bell, Utensils, Clock, Volume2, VolumeX, Maximize, History } from "lucide-react";
import SyncStatus from "@/components/SyncStatus";
import NotifBadge from "@/components/common/NotifBadge";
import { StationSwitcher, useCompactHeader } from "@/components/common/StationSwitcher";
import { allowedStations, defaultStation, STATIONS } from "@/lib/stations";
import { acceptEvent } from "@/lib/notify";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useWebPush } from "@/hooks/useWebPush";

const PREP_TARGET_MIN = 15; // objectif de préparation (retard au-delà)

function useNow(msEvery = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), msEvery); return () => clearInterval(id); }, [msEvery]);
  return now;
}
function fmtChrono(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
function recTime(iso) { try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; } }

const COLS = [
  { key: "ACCEPTEE", title: "À COMMENCER", accent: "#C69C4A", next: "EN_PREPARATION", cta: "Commencer", icon: ChefHat },
  { key: "EN_PREPARATION", title: "EN PRÉPARATION", accent: "#60A5FA", next: "PRETE", cta: "Prêt", icon: Bell },
  { key: "PRETE", title: "PRÊTES", accent: "#34D399", next: null, cta: null, icon: Utensils },
];

function KDSCard({ order, col, now, onAdvance, onLoss }) {
  const base = order.status === "EN_PREPARATION" ? (order.preparation_started_at || order.created_at) : order.created_at;
  const sec = Math.max(0, Math.floor((now - new Date(base).getTime()) / 1000));
  const late = order.status === "EN_PREPARATION" && sec > PREP_TARGET_MIN * 60;
  const num = order.order_number.replace("#", "");
  const code = order.order_code || order.order_number;
  return (
    <div data-testid={`kds-card-${num}`} className={`rounded-2xl bg-[#111F1A] border-2 p-4 mb-4 ${order.status === "REFUSEE" ? "border-red-600 opacity-90" : late ? "border-red-500 animate-flash-new" : order.modified_at ? "border-amber-500" : "border-[#1B3028]"}`}>
      {order.status === "REFUSEE" && <div data-testid={`kds-cancelled-${num}`} className="mb-2 rounded-lg bg-red-600 text-white text-center font-extrabold uppercase tracking-widest py-1.5 animate-flash-new">✕ ANNULÉE — ne pas préparer</div>}
      {order.status !== "REFUSEE" && order.is_addition && <div data-testid={`kds-addition-${num}`} className="mb-2 rounded-lg bg-[#C69C4A] text-[#0C1713] text-center font-extrabold uppercase tracking-widest text-xs py-1">Ajout · complément de {order.parent_order_number}</div>}
      {order.status !== "REFUSEE" && order.modified_at && <div data-testid={`kds-modified-${num}`} className="mb-2 rounded-lg bg-amber-500 text-[#0C1713] text-center font-extrabold uppercase tracking-widest py-1.5">✎ MODIFIÉE{order.modified_by ? ` · ${order.modified_by}` : ""} — vérifier les articles</div>}
      <div className="flex items-center justify-between">
        <span className="font-serif-display text-3xl text-white leading-none">T{order.table_number}</span>
        <span className="text-sm font-mono text-stone-400">{code}</span>
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-xs text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {recTime(order.created_at)}</span>
        <span className={`text-lg font-bold font-mono ${late ? "text-red-400" : "text-[#E7C97A]"}`}>
          {fmtChrono(sec)}{late ? ` · RETARD +${Math.floor((sec - PREP_TARGET_MIN * 60) / 60)} MIN` : ""}
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {order.items.map((it, k) => (
          <div key={k} className="rounded-lg bg-white/[0.04] border-l-4 border-[#C69C4A]/60 px-3 py-2">
            <p className="text-white font-extrabold text-2xl leading-tight">{it.quantity}× {it.name}</p>
            <div className="text-base text-stone-300 pl-2 mt-1 leading-snug">
              {(it.formula || []).map((f, i) => <div key={`f${i}`}>· {f.course}: <b className="text-white">{f.choice}</b></div>)}
              {(it.options || []).map((o, i) => <span key={`o${i}`} className="mr-3">· {o.value}</span>)}
              {it.cooking && <span className="mr-3">· cuisson : <b className="text-white">{it.cooking}</b></span>}
              {it.side && <span className="mr-3">· {it.side}</span>}
              {(it.supplements || []).map((s, i) => <span key={i} className="mr-3">· + {s.name}</span>)}
              {it.instructions && <p className="text-[#E7C97A] font-bold text-lg mt-1">⚠ {it.instructions}</p>}
            </div>
          </div>
        ))}
      </div>
      {order.guest_name && <p className="text-sm text-stone-500 mt-2">👤 {order.guest_name}</p>}
      {(order.kitchen_losses || []).length > 0 && (
        <p data-testid={`kds-losses-${num}`} className="mt-2 text-xs text-amber-300 font-semibold">⚠ Perte déclarée : {order.kitchen_losses.map((l) => `${l.quantity}× ${l.name}`).join(", ")}</p>
      )}
      {order.status === "EN_PREPARATION" && onLoss && (
        <button data-testid={`kds-loss-btn-${num}`} onClick={() => onLoss(order)} className="mt-2 w-full h-10 rounded-xl border border-amber-500/40 text-amber-300 text-sm font-semibold hover:bg-amber-500/10 active:scale-[0.98]">Déclarer une perte</button>
      )}
      {col.next && order.status !== "REFUSEE" ? (
        <button data-testid={`kds-advance-${num}`} onClick={() => onAdvance(order.id, col.next)}
          className="mt-3 w-full h-14 rounded-xl font-extrabold text-lg text-[#0C1713] active:scale-[0.98] transition-transform"
          style={{ backgroundColor: col.accent }}>
          {col.cta}
        </button>
      ) : (
        (() => {
          const rs = order.ready_at ? Math.max(0, Math.floor((now - new Date(order.ready_at).getTime()) / 1000)) : 0;
          const lateS = !order.taken_for_service_at && rs >= 180;
          return (
            <div data-testid={`kds-ready-${num}`} className={`mt-3 w-full h-14 rounded-xl grid place-items-center font-extrabold text-base border ${lateS ? "text-red-300 bg-red-500/10 border-red-500/40 animate-flash-new" : "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"}`}>
              {order.taken_for_service_at ? `🙋 PRISE EN CHARGE · ${order.taken_for_service_by || ""}` : `🟢 PRÊTE — EN ATTENTE${order.ready_at ? ` · ${fmtChrono(rs)}` : ""}`}
            </div>
          );
        })()
      )}
    </div>
  );
}

export default function KDSPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const compact = useCompactHeader();
  // Garde RBAC côté UI (défense en profondeur) : un rôle sans accès KDS est redirigé vers son poste par défaut.
  useEffect(() => {
    if (user && !allowedStations(user).includes("kds")) navigate(STATIONS[defaultStation(user)].route, { replace: true });
  }, [user, navigate]);
  const [slug, setSlug] = useState(null);
  const [view, setView] = useState("board");
  const [lossOrder, setLossOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [audioReady, setAudioReady] = useState(isAudioUnlocked());
  useEffect(() => subscribeAudio(setAudioReady), []);
  const soundRef = useRef(true); soundRef.current = soundOn;
  const now = useNow();

  useEffect(() => {
    const s = user?.restaurant_slug || null;
    setSlug(s || null);
  }, [user]);
  // Même correctif que le POS : une commande ACCEPTÉE récupérée par polling/resync (et non par le
  // socket) doit aussi sonner. acceptEvent évite tout doublon.
  const primedRef = useRef(false);
  const load = () => slug && api.listOrders(slug).then((list) => {
    setOrders(list);
    if (!primedRef.current) { primedRef.current = true; return; }
    list.forEach((o) => { if (o.status === "ACCEPTEE" && soundRef.current && acceptEvent(`order:${o.id}:ACCEPTEE`)) playChime(); });
  }).catch(() => {});
  useWakeLock();
  useWebPush(slug);
  // Chargement initial (démarrage / F5 / changement de restaurant). Ensuite, temps réel via WebSocket.
  useEffect(() => { if (slug) { load(); flushQueue(load); } /* eslint-disable-next-line */ }, [slug]);
  const [queued, setQueued] = useState(0);
  useEffect(() => onQueueChange(setQueued), []);
  const feats = useFeatures(slug);

  const { connected } = useSocket(slug ? [`resto:${slug}`] : [], {
    features_updated: feats.reload,
    new_order: (o) => { setOrders((p) => p.find((x) => x.id === o.id) ? p : [o, ...p]); },
    order_updated: (o) => {
      setOrders((p) => p.find((x) => x.id === o.id) ? p.map((x) => (x.id === o.id ? o : x)) : [o, ...p]);
      // La cuisine ne "reçoit" (son + apparition) la commande qu'une fois ACCEPTÉE par la caisse.
      if (o.status === "ACCEPTEE" && soundRef.current && acceptEvent(`order:${o.id}:ACCEPTEE`)) playChime();
    },
    resync: () => { flushQueue(load); load(); },
  });

  // Filet de réconciliation : recharge périodique INDÉPENDANTE de l'état "connected".
  // Un socket "zombie" peut garder connected=true sans recevoir les events (ex : un plat marqué
  // "servi" qui reste bloqué en PRÊTE). Ce backstop garantit la mise à jour sans F5.
  useEffect(() => {
    if (!slug) return;
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [slug]);

  const advance = async (id, status) => {
    // Optimistic + offline-safe: apply locally, queue if no network, resync on reconnect.
    setOrders((p) => p.map((o) => (o.id === id ? { ...o, status } : o)));
    try {
      const res = await submitOrderStatus(id, status);
      if (res.order) setOrders((p) => p.map((o) => (o.id === id ? res.order : o)));
    } catch (e) { load(); }
  };
  const enableSound = () => { unlockAudio(); setAudioReady(true); playChime(); };
  const goFull = () => { const el = document.documentElement; if (!document.fullscreenElement) el.requestFullscreen?.(); else document.exitFullscreen?.(); };

  const byCol = useMemo(() => {
    const m = { ACCEPTEE: [], EN_PREPARATION: [], PRETE: [] };
    const cutoff = Date.now() - 5 * 60 * 1000;
    orders.forEach((o) => {
      if (m[o.status]) m[o.status].push(o);
      // Annulée après prise en charge : reste affichée 5 min en rouge dans sa colonne d'origine.
      else if (o.status === "REFUSEE" && o.cancelled && o.refused_at && new Date(o.refused_at).getTime() > cutoff) {
        const col = o.ready_at ? "PRETE" : o.preparation_started_at ? "EN_PREPARATION" : "ACCEPTEE";
        m[col].push(o);
      }
    });
    Object.values(m).forEach((l) => l.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)));
    return m;
  }, [orders]);

  if (!feats.has("kds")) return <div className="min-h-screen bg-[#0A1712] p-6"><FeatureOff label="Cuisine KDS" /></div>;
  const historyCount = buildHistory(orders).filter((r) => r.status !== "REFUSEE").reduce((s, r) => s + r.qty, 0);
  return (
    <div className="dark-scroll min-h-screen bg-[#0A1712] text-stone-100 flex flex-col" style={{ overflowX: "clip" }}>
      <header className={`flex items-center justify-between px-5 bg-[#08110E] border-b border-[#1B3028] sticky top-0 z-20 transition-[padding] duration-200 ${compact ? "py-1.5" : "py-3"}`}>
        <div className="flex items-center gap-3">
          <div className={`rounded-lg bg-[#C69C4A] text-[#0C1713] grid place-items-center font-serif-display font-bold transition-all ${compact ? "w-8 h-8 text-base" : "w-9 h-9 text-lg"}`}>N</div>
          <div>
            <p className="font-serif-display text-lg leading-none">KDS Cuisine</p>
            <p className="text-[10px] uppercase tracking-widest text-[#C69C4A]">
              <SyncStatus connected={connected} queued={queued} />
              <NotifBadge slug={slug} />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div data-testid="kds-tabs" className="flex rounded-full bg-[#111F1A] border border-[#1B3028] p-0.5 text-xs font-bold">
            <button data-testid="kds-tab-board" onClick={() => setView("board")} className={`rounded-full px-3 py-1.5 min-h-[36px] ${view === "board" ? "bg-[#C69C4A] text-[#0C1713]" : "text-stone-300"}`}>En préparation</button>
            <button data-testid="kds-tab-history" onClick={() => setView("history")} className={`rounded-full px-3 py-1.5 min-h-[36px] flex items-center gap-1.5 ${view === "history" ? "bg-[#C69C4A] text-[#0C1713]" : "text-stone-300"}`}><History className="w-3.5 h-3.5" /> Historique <span data-testid="kds-history-badge" className={`rounded-full px-1.5 text-[10px] ${view === "history" ? "bg-[#0C1713]/20" : "bg-[#1A3D32] text-[#C69C4A]"}`}>{historyCount} aujourd'hui</span></button>
          </div>
          {!audioReady && <button data-testid="kds-enable-sound" onClick={enableSound} className="rounded-full bg-[#C69C4A] text-[#0C1713] font-semibold text-xs px-3 py-2 flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5" /> Activer le son</button>}
          <button data-testid="kds-sound-toggle" onClick={() => setSoundOn((s) => !s)} className={`w-10 h-10 rounded-full grid place-items-center ${soundOn ? "bg-[#1A3D32] text-[#C69C4A]" : "bg-[#111F1A] text-stone-500"}`}>{soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</button>
          <button data-testid="kds-fullscreen" onClick={goFull} className="w-10 h-10 rounded-full grid place-items-center bg-[#111F1A] text-stone-300"><Maximize className="w-5 h-5" /></button>
          <StationSwitcher current="kds" slug={slug} />
        </div>
      </header>
      {view === "history" ? <KDSHistory orders={orders} slug={slug} /> : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 flex-1">
        {COLS.map((col) => (
          <div key={col.key} data-testid={`kds-col-${col.key}`} className="min-w-0">
            <div className="flex items-center justify-between mb-3 sticky top-[68px] bg-[#0A1712] py-1 z-10">
              <h2 className="text-lg font-extrabold tracking-wide flex items-center gap-2" style={{ color: col.accent }}><col.icon className="w-5 h-5" /> {col.title}</h2>
              <span className="text-sm font-bold text-stone-400 bg-[#111F1A] rounded-full px-2.5 py-0.5">{byCol[col.key].length}</span>
            </div>
            {byCol[col.key].length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#1B3028] py-10 text-center text-sm text-stone-600">Aucune commande</div>
            ) : byCol[col.key].map((o) => <KDSCard key={o.id} order={o} col={col} now={now} onAdvance={advance} onLoss={setLossOrder} />)}
          </div>
        ))}
      </div>
      )}
      {lossOrder && <KitchenLossDialog order={lossOrder} onClose={(ok) => { setLossOrder(null); if (ok) load(); }} />}
    </div>
  );
}
